import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaUsers } from "react-icons/fa";
import { useBuilding } from "../context/BuildingContext";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppEmptyState, SectionLabel } from "../components/app";
import AsambleaCard from "../components/asambleas/AsambleaCard";
import HistorialAsambleas from "../components/asambleas/HistorialAsambleas";
import DomicilioElectronicoCard from "../components/asambleas/DomicilioElectronicoCard";
import { appColors, appType, appSpacing } from "../utils/appTheme";
import { asambleaPropietario } from "../utils/asambleas/api";

/**
 * Asambleas del residente: solo las de edificios donde es propietario vigente.
 * Arriba las próximas (tarjeta con "¿Vas a asistir?" y "Entrar") y el domicilio
 * electrónico; abajo el historial, una fila por asamblea con el orden del día,
 * el acta y la grabación. En desktop usa el ancho de la pantalla.
 */
export default function AsambleasResidente({ goBack } = {}) {
  const { t } = useTranslation("asambleas");
  const { selectedBuilding, userUnitsInBuilding } = useBuilding();
  const isMobile = useIsMobile();
  // Solo propietarios vigentes (el servidor aplica la misma regla): a un
  // inquilino o a un ex propietario se le explica en vez de mostrar "vacío".
  const isCurrentOwner = (userUnitsInBuilding || []).some(
    (u) => u.userRole === "Propietario" && !u.isHistorical
  );
  const buildingId = selectedBuilding ? selectedBuilding.buildingid : null;
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [actas, setActas] = useState([]);
  const [cargandoActas, setCargandoActas] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await asambleaPropietario("listar", buildingId != null ? { buildingId } : {});
      setLista(res.asambleas || []);
    } catch (e) {
      setLista([]);
    } finally {
      setCargando(false);
    }
  }, [buildingId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Actas publicadas: solo se piden para propietarios vigentes. Si falla (o el
  // servidor todavía no tiene la operación), la pantalla sigue sin ellas.
  useEffect(() => {
    if (!isCurrentOwner) {
      setActas([]);
      setCargandoActas(false);
      return undefined;
    }
    let vigente = true; // descarta respuestas viejas si cambia el edificio
    setCargandoActas(true);
    asambleaPropietario("actas", buildingId != null ? { buildingId } : {})
      .then((res) => {
        if (vigente) setActas((res && res.actas) || []);
      })
      .catch(() => {
        if (vigente) setActas([]);
      })
      .finally(() => {
        if (vigente) setCargandoActas(false);
      });
    return () => {
      vigente = false;
    };
  }, [isCurrentOwner, buildingId]);

  const proximas = lista.filter((a) => a.estado === "convocada" || a.estado === "en_curso");
  // Las actas de una asamblea de la lista van en su fila; las demás (cargadas a mano), sueltas.
  // Si `listar` no trajo el acta de la fila (un servidor anterior), se usa la de `actas`.
  const actaPorAsamblea = new Map(actas.filter((x) => x.asambleaId).map((x) => [x.asambleaId, x]));
  const anteriores = lista
    .filter((a) => !(a.estado === "convocada" || a.estado === "en_curso"))
    .map((a) => (a.acta || !actaPorAsamblea.has(a.id) ? a : { ...a, acta: actaPorAsamblea.get(a.id) }));
  const idsLista = new Set(lista.map((a) => a.id));
  const actasSueltas = actas.filter((x) => !x.asambleaId || !idsLista.has(x.asambleaId));

  return (
    // AppScreen corta en 480 px (el ancho del celular): en desktop, el historial usa la pantalla.
    <AppScreen maxWidth={isMobile ? undefined : 1140} header={<AppHeader title={t("title")} subtitle={t("subtitle")} onBack={goBack} />}>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: appSpacing.section }}>
        {/* En desktop, la próxima asamblea y el domicilio electrónico lado a lado. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: appSpacing.section, alignItems: "flex-start" }}>
          {proximas.length > 0 && !cargando && (
            <div style={{ flex: isMobile ? "1 1 100%" : "1 1 460px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              <SectionLabel>{t("estado.convocada")}</SectionLabel>
              {proximas.map((a) => (
                <AsambleaCard key={a.id} a={a} onCambio={cargar} />
              ))}
            </div>
          )}
          {isCurrentOwner && <DomicilioElectronicoCard style={{ flex: isMobile ? "1 1 100%" : "0 1 360px", minWidth: 0 }} />}
        </div>
        {!isCurrentOwner && !cargando && lista.length === 0 ? (
          <AppEmptyState icon={<FaUsers />} title={t("soloPropietarios.title")} description={t("soloPropietarios.description")} />
        ) : cargando || cargandoActas ? (
          <div style={{ ...appType.body, color: appColors.ink3 }}>{t("loading")}</div>
        ) : lista.length === 0 && actas.length === 0 ? (
          <AppEmptyState icon={<FaUsers />} title={t("empty.title")} description={t("empty.description")} />
        ) : (
          <HistorialAsambleas asambleas={anteriores} actas={actasSueltas} esMovil={isMobile} />
        )}
      </div>
    </AppScreen>
  );
}
